import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/stores/authStore'
import { firmApi } from '@/services/api'
import { Building2, User, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'America/Vancouver', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Europe/Amsterdam', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Dubai',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
]

const CURRENCIES = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { code: 'ZAR', label: 'ZAR — South African Rand' },
]

export default function FirmSettings() {
  const { currentFirm, user } = useAuth()
  const { setCurrentFirm, firms, setFirms } = useAuthStore()

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: currentFirm?.name || '',
    legal_name: currentFirm?.legal_name || '',
    timezone: currentFirm?.timezone || 'UTC',
    currency_code: currentFirm?.currency_code || 'USD',
  })

  function startEdit() {
    setForm({
      name: currentFirm?.name || '',
      legal_name: currentFirm?.legal_name || '',
      timezone: currentFirm?.timezone || 'UTC',
      currency_code: currentFirm?.currency_code || 'USD',
    })
    setEditing(true)
  }

  async function handleSave() {
    if (!currentFirm) return
    if (!form.name.trim()) { toast.error('Firm name is required'); return }
    setSaving(true)
    try {
      const res = await firmApi.update(currentFirm.id, form)
      const updated = res.data
      setCurrentFirm(updated)
      setFirms(firms.map((f) => (f.id === updated.id ? updated : f)))
      setEditing(false)
      toast.success('Firm settings saved')
    } catch {
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Settings</h1>
        <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Manage your firm and account settings</p>
      </div>

      {/* Firm Details */}
      <Card>
        <CardHeader className="border-b px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-4 h-4 text-[#0078C8]" />
              <CardTitle className="text-[13px] font-bold">Firm Details</CardTitle>
            </div>
            {!editing && (
              <Button variant="outline" size="sm" onClick={startEdit} className="text-xs h-7">
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-5">
          {editing ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Firm Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Acme Accounting Ltd"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Legal Name <span className="text-[rgba(0,10,30,0.75)] font-normal">(optional)</span></Label>
                <Input
                  value={form.legal_name}
                  onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                  placeholder="Legal registered name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <select value={form.currency_code} onChange={(e) => setForm({ ...form, currency_code: e.target.value })} className="input">
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Timezone</Label>
                  <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="input">
                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
              </div>
              <Separator />
              <div className="flex gap-3">
                <Button onClick={handleSave} disabled={saving}>
                  <Check className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <dl className="space-y-3">
              {[
                { label: 'Firm Name', value: currentFirm?.name },
                { label: 'Legal Name', value: currentFirm?.legal_name },
                { label: 'Currency', value: currentFirm?.currency_code },
                { label: 'Timezone', value: currentFirm?.timezone },
              ].map((item) => (
                <div key={item.label} className="flex items-center">
                  <dt className="w-32 text-[13px] text-[rgba(0,10,30,0.75)]">{item.label}</dt>
                  <dd className="text-[13px] text-[#000A1E]">{item.value || <span className="text-[#CCCED2]">&mdash;</span>}</dd>
                </div>
              ))}
            </dl>
          )}
        </CardContent>
      </Card>

      {/* Account */}
      <Card>
        <CardHeader className="border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-[rgba(0,10,30,0.75)]" />
            <CardTitle className="text-[13px] font-bold">Your Account</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <dl className="space-y-3">
            {[
              { label: 'Name', value: user?.full_name },
              { label: 'Email', value: user?.email },
            ].map((item) => (
              <div key={item.label} className="flex items-center">
                <dt className="w-32 text-[13px] text-[rgba(0,10,30,0.75)]">{item.label}</dt>
                <dd className="text-[13px] text-[#000A1E]">{item.value || <span className="text-[#CCCED2]">&mdash;</span>}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </div>
  )
}
